'use client';

import React, { useEffect, useState, useRef } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { ArrowUp, ArrowDown } from 'lucide-react';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Filler
);

// Yahoo Finance Exact Cash Indices
const INDICES = [
  { label: 'NIFTY 50',  symbol: '^NSEI' },
  { label: 'SENSEX',    symbol: '^BSESN' },
  { label: 'NASDAQ',    symbol: '^IXIC' }, // Nasdaq Composite
  { label: 'S&P 500',   symbol: '^GSPC' },
  { label: 'DOW JONES', symbol: '^DJI' },
  { label: 'FTSE 100',  symbol: '^FTSE' },
];

const TIMEFRAMES = [
  { label: '1D', range: '1d' },
  { label: '5D', range: '5d' },
  { label: '1M', range: '1mo' },
  { label: '6M', range: '6mo' },
  { label: '1Y', range: '1y' },
  { label: '5Y', range: '5y' },
];

function CustomChart({ symbol, active }: { symbol: string; active: boolean }) {
  const [range, setRange] = useState('1mo');
  const [data, setData] = useState<any[]>([]);
  const [meta, setMeta] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const chartRef = useRef<any>(null);

  useEffect(() => {
    if (!active) return;
    
    let isMounted = true;
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await fetch(`/api/market/chart?symbol=${encodeURIComponent(symbol)}&range=${range}`);
        const json = await res.json();
        
        if (json.success && isMounted) {
          setData(json.data);
          setMeta(json.meta);
        }
      } catch (error) {
        console.error("Chart fetch error:", error);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchData();
    return () => { isMounted = false; };
  }, [symbol, range, active]);

  if (!active) return null;

  const isPositive = meta ? meta.change >= 0 : true;
  const color = isPositive ? 'rgba(34, 197, 94, 1)' : 'rgba(239, 68, 68, 1)';
  const bgColor = isPositive ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)';

  const chartData = {
    labels: data.map(d => {
      const date = new Date(d.time);
      if (range === '1d' || range === '5d') {
        return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
      }
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: range === '5y' ? 'numeric' : undefined });
    }),
    datasets: [
      {
        fill: true,
        data: data.map(d => d.price),
        borderColor: color,
        backgroundColor: (context: any) => {
          const ctx = context.chart.ctx;
          const gradient = ctx.createLinearGradient(0, 0, 0, context.chart.height);
          gradient.addColorStop(0, bgColor);
          gradient.addColorStop(1, 'rgba(255, 255, 255, 0)');
          return gradient;
        },
        borderWidth: 2,
        pointRadius: 0,
        pointHoverRadius: 4,
        pointBackgroundColor: color,
        tension: 0.1
      }
    ]
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        mode: 'index' as const,
        intersect: false,
        backgroundColor: 'rgba(0,0,0,0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: 'rgba(255,255,255,0.1)',
        borderWidth: 1,
        callbacks: {
          label: (context: any) => {
            return `Price: ${context.parsed.y.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
          }
        }
      }
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { maxTicksLimit: 6, color: '#9ca3af', font: { size: 10 } }
      },
      y: {
        position: 'right' as const,
        grid: { color: 'rgba(0,0,0,0.05)' },
        ticks: { color: '#9ca3af', font: { size: 10 } }
      }
    },
    interaction: {
      mode: 'nearest' as const,
      axis: 'x' as const,
      intersect: false,
    },
  };

  return (
    <div className="flex flex-col h-full bg-card rounded-xl overflow-hidden border border-border">
      {/* Chart Header (Stats & Timeframes) */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 p-4 md:p-6 border-b border-border/50">
        <div className="flex flex-col">
          {meta ? (
            <>
              <div className="text-2xl font-black font-mono tracking-tight text-foreground">
                {meta.regularMarketPrice.toLocaleString(undefined, { maximumFractionDigits: 2 })}
              </div>
              <div className={`flex items-center gap-1 text-sm font-bold font-mono ${isPositive ? 'text-green-600' : 'text-red-500'}`}>
                {isPositive ? <ArrowUp className="w-3.5 h-3.5" /> : <ArrowDown className="w-3.5 h-3.5" />}
                {Math.abs(meta.change).toFixed(2)} ({Math.abs(meta.changePercent).toFixed(2)}%)
              </div>
            </>
          ) : (
            <div className="animate-pulse flex flex-col gap-2">
              <div className="h-8 w-32 bg-muted rounded" />
              <div className="h-4 w-24 bg-muted rounded" />
            </div>
          )}
        </div>

        <div className="flex items-center gap-1 bg-muted/30 p-1 rounded-lg border border-border/50 overflow-x-auto">
          {TIMEFRAMES.map((tf) => (
            <button
              key={tf.range}
              onClick={() => setRange(tf.range)}
              className={`px-3 py-1 text-[11px] font-black uppercase tracking-widest rounded-md transition-colors ${
                range === tf.range 
                  ? 'bg-background text-foreground shadow-sm border border-border/50' 
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {tf.label}
            </button>
          ))}
        </div>
      </div>

      {/* Chart Area */}
      <div className="flex-1 p-4 md:p-6 relative">
        {loading && data.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center bg-card/50 backdrop-blur-sm z-10">
            <div className="w-6 h-6 border-2 border-brand border-t-transparent rounded-full animate-spin" />
          </div>
        )}
        {data.length > 0 && (
          <Line ref={chartRef} data={chartData} options={options} />
        )}
      </div>
    </div>
  );
}

export default function MarketsPageClient() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [time, setTime] = useState('');

  useEffect(() => {
    const tick = () => setTime(new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true }));
    tick();
    const iv = setInterval(tick, 1000);
    return () => clearInterval(iv);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      {/* Terminal Header */}
      <div className="border-b border-border bg-card/50 px-4 md:px-8 py-4">
        <div className="max-w-screen-2xl mx-auto flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
            <div>
              <h1 className="text-lg font-black uppercase tracking-[0.2em] text-foreground">WPP Live Markets</h1>
              <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground/60">Global Intelligence Terminal • Official Feeds</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <div className="font-mono text-sm font-bold text-foreground tabular-nums">{time}</div>
              <div className="text-[9px] uppercase tracking-widest text-muted-foreground/50 font-bold">Live Clock</div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-screen-2xl mx-auto px-4 md:px-8 py-6 space-y-8">
        
        {/* Global Indices - CUSTOM WPP CHART ENGINE */}
        <section>
          <div className="flex items-center gap-2 mb-4">
            <div className="w-1 h-4 bg-brand" />
            <h2 className="text-[11px] font-black uppercase tracking-[0.3em] text-muted-foreground">Global Indices (Official Cash Feeds)</h2>
          </div>
          
          <div className="flex flex-wrap gap-2 mb-4">
            {INDICES.map((idx, i) => (
              <button
                key={idx.symbol}
                onClick={() => setActiveIndex(i)}
                className={`px-3 py-1.5 rounded-md text-[10px] font-black uppercase tracking-widest border transition-all ${
                  activeIndex === i
                    ? 'bg-brand text-white border-brand shadow-sm'
                    : 'border-border text-muted-foreground hover:border-brand/50 hover:text-foreground bg-card'
                }`}
              >
                {idx.label}
              </button>
            ))}
          </div>
          
          <div className="w-full" style={{ height: '450px' }}>
            {INDICES.map((idx, i) => (
              <CustomChart
                key={idx.symbol}
                symbol={idx.symbol}
                active={activeIndex === i}
              />
            ))}
          </div>
        </section>

        {/* 🚀 HIGH-CONVERTING TRADINGVIEW AFFILIATE BANNER */}
        <div className="relative w-full rounded-2xl overflow-hidden bg-gradient-to-r from-gray-900 via-gray-800 to-black text-white p-6 md:p-8 shadow-xl border border-gray-700/50 flex flex-col md:flex-row items-center justify-between gap-6 group">
          <div className="absolute inset-0 bg-[url('https://s3.tradingview.com/tv.js')] opacity-0"></div>
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-brand opacity-80" />
          
          <div className="flex-1 space-y-2 z-10 text-center md:text-left">
            <h3 className="text-xl md:text-2xl font-black uppercase tracking-widest text-white">
              Need Advanced Candlesticks & Pro Tools?
            </h3>
            <p className="text-sm md:text-base text-gray-300 font-medium">
              Take your analysis to the next level. Get real-time premium charts, 100+ technical indicators, and multi-screen setups. Join 50 million traders today.
            </p>
          </div>
          
          <div className="z-10 shrink-0">
            <a 
              href="https://in.tradingview.com/?aff_id=167158&source=worldprimepost" 
              target="_blank" 
              rel="noopener noreferrer nofollow sponsored"
              className="inline-flex items-center justify-center px-8 py-4 text-sm font-black uppercase tracking-widest text-black bg-white hover:bg-gray-100 rounded-full transition-transform transform hover:scale-105 shadow-[0_0_20px_rgba(255,255,255,0.3)]"
            >
              Unlock TradingView Pro
              <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </a>
            <p className="text-[9px] uppercase tracking-widest text-gray-500 text-center mt-2 font-bold">
              Special offer for WPP Readers
            </p>
          </div>
        </div>

        {/* Forex + Crypto (Still using TV widgets as they never block these asset classes) */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 pt-4 border-t border-border/40">
          <section>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-1 h-4 bg-brand" />
              <h2 className="text-[11px] font-black uppercase tracking-[0.3em] text-muted-foreground">Forex — Major Pairs</h2>
            </div>
            <div className="rounded-xl border border-border overflow-hidden shadow-sm bg-card" style={{ height: '400px' }}>
              <iframe
                src="https://s.tradingview.com/embed-widget/forex-cross-rates/?locale=en#%7B%22currencies%22%3A%5B%22EUR%22%2C%22USD%22%2C%22JPY%22%2C%22GBP%22%2C%22CHF%22%2C%22AUD%22%2C%22CAD%22%2C%22INR%22%5D%2C%22colorTheme%22%3A%22light%22%2C%22isTransparent%22%3Afalse%2C%22width%22%3A%22100%25%22%2C%22height%22%3A%22100%25%22%7D"
                style={{ width: '100%', height: '100%', border: 'none' }}
                allowTransparency={true}
                frameBorder={0}
                loading="lazy"
              />
            </div>
          </section>
          <section>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-1 h-4 bg-brand" />
              <h2 className="text-[11px] font-black uppercase tracking-[0.3em] text-muted-foreground">Crypto — Top Assets</h2>
            </div>
            <div className="rounded-xl border border-border overflow-x-auto overflow-y-hidden shadow-sm bg-card w-full scrollbar-thin scrollbar-thumb-border scrollbar-track-transparent">
              <div style={{ minWidth: '480px', height: '600px' }}>
                <iframe
                  src="https://s.tradingview.com/embed-widget/screener/?locale=en#%7B%22width%22%3A%22100%25%22%2C%22height%22%3A%22100%25%22%2C%22defaultColumn%22%3A%22overview%22%2C%22defaultScreen%22%3A%22general%22%2C%22market%22%3A%22crypto%22%2C%22showToolbar%22%3Atrue%2C%22colorTheme%22%3A%22light%22%2C%22locale%22%3A%22en%22%7D"
                  style={{ width: '100%', height: '100%', border: 'none' }}
                  allowTransparency={true}
                  frameBorder={0}
                  loading="lazy"
                />
              </div>
            </div>
          </section>
        </div>

        {/* Economic Calendar */}
        <section>
          <div className="flex items-center gap-2 mb-4">
            <div className="w-1 h-4 bg-brand" />
            <h2 className="text-[11px] font-black uppercase tracking-[0.3em] text-muted-foreground">Global Economic Calendar</h2>
          </div>
          <div className="rounded-xl border border-border overflow-hidden shadow-sm bg-card" style={{ height: '500px' }}>
            <iframe
              src="https://s.tradingview.com/embed-widget/events/?locale=en#%7B%22colorTheme%22%3A%22light%22%2C%22isTransparent%22%3Afalse%2C%22width%22%3A%22100%25%22%2C%22height%22%3A%22100%25%22%2C%22importanceFilter%22%3A%22-1%2C0%2C1%22%2C%22countryFilter%22%3A%22us%2Ceu%2Cgb%2Ccn%2Cin%2Cjp%22%7D"
              style={{ width: '100%', height: '100%', border: 'none' }}
              allowTransparency={true}
              frameBorder={0}
              loading="lazy"
            />
          </div>
        </section>

        {/* Intelligence Disclaimer */}
        <section className="mt-8 mb-4">
          <div className="bg-muted/30 border border-border rounded-xl p-6 text-center max-w-4xl mx-auto">
            <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground mb-3 flex items-center justify-center gap-2">
              <span className="w-8 h-[1px] bg-border"></span>
              Market Intelligence Disclaimer
              <span className="w-8 h-[1px] bg-border"></span>
            </h3>
            <p className="text-xs text-muted-foreground/80 leading-relaxed">
              The market data, charts, and intelligence provided on WorldPrimePost are strictly for informational and educational purposes. They do not constitute financial, investment, or trading advice. While we strive for accuracy, market data may be delayed and is not guaranteed to be complete. Always consult with a certified financial advisor before making any investment decisions. WorldPrimePost and its affiliates are not liable for any financial losses incurred based on this information.
            </p>
          </div>
        </section>

      </div>
    </div>
  );
}
